import { injectable, inject } from 'inversify';
import { GTD_DOMAIN_SYMBOLS } from '../../../../infrastructure/di/GTDDomainSymbols';
import { InboxItem } from "../../domain/aggregates/InboxItem";
import { InboxItemAggregateRepository } from "../../infrastructure/repositories/InboxItemAggregateRepository";
import { GTDProcessingWorkflowService, InboxProcessingDecision, GTDProcessingResult } from "../../domain/services/GTDProcessingWorkflowService";
import { NextActionAggregateRepository } from "../../../project-management/infrastructure/repositories/NextActionAggregateRepository";
import {
    CreateInboxItemCommand,
    CreateInboxItemResult
} from "../commands/CreateInboxItemCommand";
import {
    DeleteInboxItemCommand,
    DeleteInboxItemResult
} from "../commands/DeleteInboxItemCommand";
import {
    ProcessInboxItemCommand,
    ProcessInboxItemResult
} from "../commands/ProcessInboxItemCommand";

/**
 * Unified Inbox Item Application Service
 * 
 * Following Eric Evans Application Service pattern - single point of entry 
 * for all inbox item operations. Coordinates domain operations and provides
 * a clean interface for external clients.
 * 
 * Benefits:
 * - Single responsibility per operation
 * - Consistent error handling
 * - Transaction boundaries
 * - Clear API for MCP/UI layers
 */
@injectable()
export class InboxItemApplicationService {
    private readonly workflowService: GTDProcessingWorkflowService;

    constructor(
        @inject(GTD_DOMAIN_SYMBOLS.InboxItemAggregateRepository) 
        private readonly repository: InboxItemAggregateRepository,
        @inject(GTD_DOMAIN_SYMBOLS.NextActionAggregateRepository)
        _nextActionRepository: NextActionAggregateRepository
    ) {
        this.workflowService = new GTDProcessingWorkflowService();
    }

    /**
     * Create a new inbox item - GTD Capture workflow
     */
    async createInboxItem(command: CreateInboxItemCommand): Promise<CreateInboxItemResult> {
        try {
            // 1. Create domain aggregate using factory
            const item = InboxItem.capture(
                command.originalContent,
                command.capturedByPersonId
            );

            // 2. Persist via repository
            await this.repository.save(item);

            // 3. Return structured result
            return {
                success: true,
                itemId: item.id,
                capturedAt: item.capturedAt.toISOString(),
                message: `GTD inbox item created successfully`
            };

        } catch (error) {
            throw error; // Let caller handle formatting
        }
    }

    /**
     * Delete an inbox item
     */
    async deleteInboxItem(command: DeleteInboxItemCommand): Promise<DeleteInboxItemResult> {
        try {
            // 1. Load the aggregate
            const item = await this.repository.findById(command.itemId);
            if (!item) {
                throw new Error(`Inbox item with ID ${command.itemId} not found`);
            }

            // 2. Apply domain rules (if any)
            // TODO: Add deletion validation rules

            // 3. Delete from repository
            await this.repository.delete(command.itemId);

            // 4. Return result
            return {
                success: true,
                itemId: command.itemId,
                deletedAt: new Date().toISOString(),
                message: `GTD inbox item deleted successfully`
            };

        } catch (error) {
            throw error;
        }
    }

    /**
     * Process an inbox item - GTD Clarification workflow
     */
    async processInboxItem(command: ProcessInboxItemCommand): Promise<ProcessInboxItemResult> {
        try {
            // 1. Load the aggregate
            const item = await this.repository.findById(command.itemId);
            if (!item) {
                throw new Error(`Inbox item with ID ${command.itemId} not found`);
            }

            // 2. Apply clarification using domain method
            if (command.clarification && command.isActionable !== undefined) {
                item.clarify(
                    command.clarification,
                    command.isActionable,
                    command.processedByPersonId
                );
            }

            // 3. Persist changes
            await this.repository.save(item);

            // 4. Return result
            return {
                success: true,
                itemId: command.itemId,
                processedAt: new Date().toISOString(),
                status: command.status || 'processed',
                message: `GTD inbox item processed successfully`
            };

        } catch (error) {
            throw error;
        }
    }

    /**
     * List inbox items with filtering
     */
    async listInboxItems(filters: {
        status?: string;
        priority?: string;
        limit?: number;
        offset?: number;
    }) {
        try {
            // 1. Apply filters and pagination
            const items = await this.repository.findByFilters({
                status: filters.status,
                priority: filters.priority,
                limit: Math.min(filters.limit || 20, 100),
                offset: filters.offset || 0
            });

            // 2. Get total count
            const totalCount = await this.repository.countByFilters({
                status: filters.status,
                priority: filters.priority
            });

            // 3. Transform to DTOs
            const itemDtos = items.map((item: InboxItem) => ({
                id: item.id,
                originalContent: item.originalContent,
                capturedAt: item.capturedAt.toISOString(),
                capturedByPersonId: item.capturedByPersonId,
                clarification: item.clarification,
                isActionable: item.isActionable,
                processingStatus: item.processingStatus.toString()
            }));

            return {
                items: itemDtos,
                totalCount,
                hasMoreResults: (filters.offset || 0) + (filters.limit || 20) < totalCount
            };

        } catch (error) {
            throw error;
        }
    }

    /**
     * Complete GTD Processing Workflow
     * 
     * This method implements David Allen's full GTD methodology:
     * 1. Clarify the inbox item
     * 2. Decide if it's actionable
     * 3. Organize into appropriate work items (NextActions, Projects, etc.)
     * 
     * This is the MISSING PIECE that completes the GTD workflow!
     */
    async completeGTDProcessing(request: {
        itemId: string;
        clarification: string;
        isActionable: boolean;
        processedByPersonId: string;
        
        // Processing decisions
        estimatedMinutes?: number;
        context?: string;
        energyLevel?: string;
        nextActionDescription?: string;
        isProject?: boolean;
        projectOutcome?: string;
        isReference?: boolean;
        isSomedayMaybe?: boolean;
    }): Promise<CompleteGTDProcessingResult> {
        try {
            // 1. Load and clarify the inbox item
            const item = await this.repository.findById(request.itemId);
            if (!item) {
                throw new Error(`Inbox item with ID ${request.itemId} not found`);
            }

            // 2. Apply clarification
            item.clarify(
                request.clarification,
                request.isActionable,
                request.processedByPersonId
            );

            // 3. Save the clarified item
            await this.repository.save(item);

            // 4. Execute GTD workflow processing
            const processingDecision: InboxProcessingDecision = {
                estimatedMinutes: request.estimatedMinutes,
                context: request.context,
                energyLevel: request.energyLevel,
                nextActionDescription: request.nextActionDescription,
                isProject: request.isProject,
                projectOutcome: request.projectOutcome,
                isReference: request.isReference,
                isSomedayMaybe: request.isSomedayMaybe
            };

            const workflowResult = this.workflowService.processInboxItem(item, processingDecision);

            // 5. Persist the created work items
            const createdWorkItems = await this.persistWorkItems(workflowResult);

            // 6. Return comprehensive result
            return {
                success: true,
                itemId: request.itemId,
                processedAt: new Date().toISOString(),
                clarification: request.clarification,
                isActionable: request.isActionable,
                workflowType: workflowResult.workflowType,
                createdWorkItems,
                message: `GTD processing completed successfully. Created ${createdWorkItems.length} work items.`
            };

        } catch (error) {
            return {
                success: false,
                itemId: request.itemId,
                error: error instanceof Error ? error.message : 'Unknown error during GTD processing',
                message: 'GTD processing failed'
            };
        }
    }

    /**
     * Persist the work items created by GTD processing
     */
    private async persistWorkItems(workflowResult: GTDProcessingResult): Promise<Array<{
        type: string;
        id: string;
        description: string;
        status: string;
    }>> {
        const persistedItems = [];

        for (const item of workflowResult.createdItems) {
            if (item.type === 'next_action') {
                // TODO: Create and save NextAction via repository
                // For now, we'll return the placeholder
                persistedItems.push({
                    type: 'next_action',
                    id: item.id,
                    description: item.description,
                    status: 'created'
                });
            } else if (item.type === 'project') {
                // TODO: Create and save Project via repository
                persistedItems.push({
                    type: 'project',
                    id: item.id,
                    description: item.description,
                    status: 'created'
                });
            } else if (item.type === 'reference_material') {
                // TODO: Create and save Reference Material
                persistedItems.push({
                    type: 'reference_material',
                    id: item.id,
                    description: item.description,
                    status: 'stored'
                });
            } else if (item.type === 'someday_maybe') {
                // TODO: Create and save Someday/Maybe item
                persistedItems.push({
                    type: 'someday_maybe',
                    id: item.id,
                    description: item.description,
                    status: 'deferred'
                });
            }
        }

        return persistedItems;
    }
}

/**
 * Result of complete GTD processing
 */
export interface CompleteGTDProcessingResult {
    success: boolean;
    itemId: string;
    processedAt?: string;
    clarification?: string;
    isActionable?: boolean;
    workflowType?: string;
    createdWorkItems?: Array<{
        type: string;
        id: string;
        description: string;
        status: string;
    }>;
    message: string;
    error?: string;
}